import { useState, useEffect } from 'react';

interface Bot {
  id: number;
  name: string;
  display_name: string;
  description: string;
}

interface Subscription {
  id: number;
  bot_id: number;
  name: string;
  display_name: string;
  subscription_start: string;
  subscription_until: string;
}

interface UseBotsReturn {
  bots: Bot[];
  subscriptions: Subscription[];
  myBots: Bot[];
  availableBots: Bot[];
  loading: boolean;
  error: string | null;
  refreshSubscriptions: () => void;
}

export const useBots = (userId: number | null): UseBotsReturn => {
  const [bots, setBots] = useState<Bot[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const fetchBots = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/bots`;
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setBots(data.bots);
      } else {
        console.error('Failed to load bots:', data);
        setError('Ошибка загрузки ботов');
      }
    } catch (err) {
      console.error('Error fetching bots:', err);
      setError('Ошибка загрузки ботов');
    }
  };

  const fetchSubscriptions = async () => {
    if (!userId) {
      setSubscriptions([]);
      return;
    }

    try {
      const url = `${process.env.REACT_APP_API_URL}/user/subscriptions`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubscriptions(data.subscriptions);
      } else {
        console.error('Failed to load subscriptions:', data);
        setError('Ошибка загрузки подписок');
      }
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      setError('Ошибка загрузки подписок');
    }
  };

  const refreshSubscriptions = () => {
    fetchSubscriptions();
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      await Promise.all([
        fetchBots(),
        fetchSubscriptions()
      ]);
      
      setLoading(false);
    };

    loadData();
  }, [userId]);

  // Вычисляем моих ботов (с активными подписками)
  const myBots = bots.filter(bot => 
    subscriptions.some(sub => {
      if (sub.bot_id !== bot.id) return false;
      
      const endDate = new Date(sub.subscription_until);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Сбрасываем время для корректного сравнения
      
      return endDate >= today; // Подписка действует весь день до конца дня
    })
  );

  // Вычисляем доступных для подписки ботов (без активных подписок)
  const availableBots = bots.filter(bot => 
    !subscriptions.some(sub => {
      if (sub.bot_id !== bot.id) return false;
      
      const endDate = new Date(sub.subscription_until);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Сбрасываем время для корректного сравнения
      
      return endDate >= today; // Подписка действует весь день до конца дня
    })
  );

  return {
    bots,
    subscriptions,
    myBots,
    availableBots,
    loading,
    error,
    refreshSubscriptions
  };
}; 